/**
 * The mouse wheel sets power (UI-014, owner 2026-09-27): wheel up for harder, down for
 * softer, 1% a notch (testers, 2026-09-27; it was 2%). Pure, so it is tested without a browser; `controls.ts` listens.
 *
 * A mouse wheel sends one event per notch; a trackpad sends a stream of small ones. How
 * many pixels a notch reports varies (100, 120, or 50 under Windows display scaling, as
 * measured in Edge), so any event big enough to be a notch counts as at least one whole
 * notch, and small ones are added up. A single notch is always exactly one step, and a
 * trackpad swipe moves power smoothly.
 */

export const WHEEL_STEP_PERCENT = 1;

/** One notch of a mouse wheel, in each of the wheel event's delta modes. */
const PIXELS_PER_NOTCH = 100;
/** A pixel delta at least this big is a mouse notch, not a trackpad's drift. */
const NOTCH_MIN_PIXELS = 40;
const LINES_PER_NOTCH = 3;

/** DOM WheelEvent.deltaMode values. */
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

/** A wheel event's movement in notches; negative is wheel up (away from the player). */
export function wheelNotches(deltaY: number, deltaMode: number): number {
  if (!Number.isFinite(deltaY)) return 0;
  if (deltaMode === DOM_DELTA_LINE) return deltaY / LINES_PER_NOTCH;
  if (deltaMode === DOM_DELTA_PAGE) return deltaY;
  const size = Math.abs(deltaY);
  if (size >= NOTCH_MIN_PIXELS) {
    return Math.sign(deltaY) * Math.max(1, Math.round(size / PIXELS_PER_NOTCH));
  }
  return deltaY / PIXELS_PER_NOTCH;
}

export interface WheelStep {
  /** The power to show, a whole percent within the slider's range. */
  readonly percent: number;
  /** Notches not yet used: a trackpad's part-notches, kept for the next event. */
  readonly carry: number;
}

/** Apply `notches` (plus what was carried) to `percent`, clamped to `min`–`max`. */
export function stepPower(
  percent: number,
  carry: number,
  notches: number,
  min: number,
  max: number,
): WheelStep {
  const total = carry + notches;
  const whole = Math.trunc(total);
  if (whole === 0) return { percent, carry: total };
  // Wheel up (negative) is harder.
  const next = percent - whole * WHEEL_STEP_PERCENT;
  const clamped = next > max ? max : next < min ? min : next;
  // At a limit, nothing is kept: turning back should respond at once.
  return { percent: clamped, carry: clamped === next ? total - whole : 0 };
}
